import { apiFetch, ApiError } from '../utils/authHeaders';

const segment = value => encodeURIComponent(String(value));
const COMMAND = /^[A-Za-z0-9_.:-]{1,128}$/;

function validateCommand(body, versionRequired = true) {
  if (!COMMAND.test(body?.command_id || '')) throw new Error('A stable command ID is required.');
  if (versionRequired && (!Number.isInteger(body.version) || body.version < 1)) {
    throw new Error('The current receipt version is required.');
  }
}

function validateDecimals(body, fields) {
  for (const field of fields) {
    if (body[field] != null && (typeof body[field] !== 'string' || !body[field].trim())) {
      throw new Error(`${field} must be a decimal string.`);
    }
  }
}

/** A client belongs to one rendered identity/site. Recreate it on context changes.
 * The callback is checked on every call, so a stale handler cannot write into a new session.
 * Command IDs belong to the caller: retry the same immutable body, never generate one here.
 */
export function createReceivingApi(getContext) {
  const initial = getContext();
  const identity = JSON.stringify([initial?.userId, initial?.orgId, initial?.locationId, initial?.department]);

  function context() {
    const current = getContext();
    if (!current?.token || !current?.userId || !current?.orgId || !current?.locationId) {
      throw new ApiError(0, 'Sign in and select an organization and site before receiving.', '/api/receiving');
    }
    if (JSON.stringify([current.userId, current.orgId, current.locationId, current.department]) !== identity) {
      throw new ApiError(0, 'Receiving context changed. Reload this view before continuing.', '/api/receiving');
    }
    return current;
  }

  async function request(path, { method = 'GET', body, signal, responseType } = {}) {
    const current = context();
    let result;
    try {
      result = await apiFetch(path, {
        method, body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body), signal, responseType,
        orgId: current.orgId, locationId: current.locationId, department: current.department, requireOrg: true,
        headers: { Authorization: `Bearer ${current.token}` },
      });
    } catch (error) {
      context(); // A stale conflict can carry the previous site's entire receipt.
      throw error;
    }
    context(); // Do not render a previous tenant's late response after a switch.
    return result;
  }

  const receiptPath = id => `/api/receiving/receipts/${segment(id)}`;
  const command = (id, action, body, options) => {
    validateCommand(body);
    return request(`${receiptPath(id)}/${action}`, { ...options, method: 'POST', body });
  };
  return {
    uploadEvidence: (receiptId, lineIndex, file, { category, classification }, options) => {
      if (!/^[a-f0-9]{64}$/.test(receiptId) || !Number.isInteger(lineIndex) || lineIndex < 0) {
        throw new Error('An exact receipt line is required for evidence.');
      }
      if (!(file instanceof File) || file.size === 0 || file.size > 50 * 1024 * 1024) {
        throw new Error('Choose a nonempty evidence file no larger than 50 MB.');
      }
      if (!category?.trim() || !['L1', 'L2', 'L3', 'L4'].includes(classification)) {
        throw new Error('Select an approved document category and classification.');
      }
      const body = new FormData();
      body.append('file', file);
      body.append('category', category.trim());
      body.append('classification', classification);
      body.append('record_ref', `receipt:${receiptId}:${lineIndex}`);
      return request('/api/documents', { ...options, method: 'POST', body });
    },
    evidencePreview: (documentId, options) => request(`/api/documents/${segment(documentId)}/view?proxy=true`,
      { ...options, responseType: 'blob' }),
    evidenceDownload: (documentId, options) => request(`/api/documents/${segment(documentId)}/download?proxy=true`,
      { ...options, responseType: 'blob' }),
    capabilities: options => request('/api/ck-ops/capabilities', options),
    ingredients: ({ after, limit = 100 } = {}, options) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (after != null) params.set('after', after);
      return request(`/api/receiving/ingredients?${params}`, options);
    },
    queue: options => request('/api/receiving/queue', options),
    receipts: ({ status, mine = false, limit = 100 } = {}, options) => {
      const params = new URLSearchParams({ mine: String(mine), limit: String(limit) });
      if (status) params.set('status', status);
      return request(`/api/receiving/receipts?${params}`, options);
    },
    receipt: (id, options) => request(receiptPath(id), options),
    summary: (id, options) => request(`${receiptPath(id)}/summary`, options),
    open: (body, options) => {
      validateCommand(body, false);
      validateDecimals(body, ['vehicle_temp_c']);
      return request('/api/receiving/receipts', { ...options, method: 'POST', body });
    },
    saveLine: (id, index, body, options) => {
      validateCommand(body);
      validateDecimals(body, ['received_qty', 'probe_temp_c']);
      if (typeof body.received_qty !== 'string') throw new Error('Enter the received quantity.');
      if (!Number.isInteger(index) || index < 0) throw new Error('A valid receipt line is required.');
      return request(`${receiptPath(id)}/lines/${index}`, { ...options, method: 'PUT', body });
    },
    submit: (id, body, options) => command(id, 'submit', body, options),
    confirm: (id, body, options) => command(id, 'confirm', body, options),
    requestCorrection: (id, body, options) => command(id, 'request-correction', body, options),
    reject: (id, body, options) => command(id, 'reject', body, options),
    putaway: options => request('/api/receiving/putaway', options),
    sections: options => request('/api/ck-ops/sections', options),
    lotHold: (id, body, options) => { validateCommand(body); return request(`/api/receiving/putaway/lots/${segment(id)}/hold`, { ...options, method: 'POST', body }); },
    lotRelease: (id, body, options) => { validateCommand(body); return request(`/api/receiving/putaway/lots/${segment(id)}/release`, { ...options, method: 'POST', body }); },
    lotMove: (id, body, options) => { validateCommand(body); return request(`/api/receiving/putaway/lots/${segment(id)}/move`, { ...options, method: 'POST', body }); },
    lotBatchNeeds: (id, options) => request(`/api/receiving/putaway/lots/${segment(id)}/batch-needs`, options),
    claims: ({ status } = {}, options) => request(`/api/receiving/claims${status ? `?status=${segment(status)}` : ''}`, options),
    claim: (id, options) => request(`/api/receiving/claims/${segment(id)}`, options),
    operandiDryRunClaim: (id, options) => request(
      `/api/receiving/claims/${segment(id)}/operandi-dry-run`, { ...options, method: 'POST' }),
    saveClaim: (id, body, options) => { validateCommand(body); return request(`/api/receiving/claims/${segment(id)}/draft`, { ...options, method: 'PUT', body }); },
    sendClaim: (id, body, options) => { validateCommand(body); return request(`/api/receiving/claims/${segment(id)}/send`, { ...options, method: 'POST', body }); },
    recordSupplierResponse: (id, body, options) => { validateCommand(body); return request(`/api/receiving/claims/${segment(id)}/supplier-response`, { ...options, method: 'POST', body }); },
    approveClaimCredit: (id, body, options) => { validateCommand(body); return request(`/api/receiving/claims/${segment(id)}/finance-approve`, { ...options, method: 'POST', body }); },
    reconcileClaimCredit: (id, creditNoteId, options) => request(
      `/api/receiving/claims/${segment(id)}/finance-reconciliation${creditNoteId ? `?credit_note_id=${segment(creditNoteId)}` : ''}`,
      options,
    ),
    confirmClaimCreditReconciliation: (id, body, options) => {
      validateCommand(body);
      return request(`/api/receiving/claims/${segment(id)}/finance-reconciliation`, { ...options, method: 'POST', body });
    },
    temperatureRules: ({ after, limit = 100 } = {}, options) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (after != null) params.set('after', after);
      return request(`/api/receiving/temperature-rules?${params}`, options);
    },
    saveTemperatureRule: (ingredientId, body, options) => {
      validateDecimals(body, ['minimum_c', 'maximum_c']);
      return request(`/api/receiving/temperature-rules/${segment(ingredientId)}`,
        { ...options, method: 'PUT', body });
    },
  };
}
